import * as React from "react";
import { filterKandidaten, type BezugKandidat } from "./bezugKandidaten";
import { useAktivSichtbar } from "./useAktivSichtbar";
import "./notizbloecke.css";

/**
 * Die Auswahl hinter dem `#` (PO 08.09.2026). Sie zeigt zuerst, was schon am
 * Arbeitsstrang hängt — die Reihenfolge der Kandidatenliste ist das Angebot.
 *
 * Bewusst kein Popover-Baustein: der Picker sitzt am Cursor im contentEditable,
 * und ein Portal mit eigenem Fokus-Management nähme dem Block die Selektion.
 */

/** Höchstzahl der angebotenen Treffer — mehr wäre eine zweite Liste, kein Menü. */
const MAX_TREFFER = 10;

const ART_LABEL: Record<BezugKandidat["art"], string> = {
    mail: "Mail",
    anruf: "Anruf",
    dokument: "Dokument",
    risiko: "Risiko",
    vertrag: "Vertrag",
    person: "Person",
    todo: "To-do",
};

/**
 * Die angebotenen Treffer in Anzeigereihenfolge. Das Schreibfeld braucht
 * dieselbe Liste, um Enter auf den markierten Treffer abzubilden (T-50).
 */
export function bezugTreffer(kandidaten: BezugKandidat[], query: string): BezugKandidat[] {
    return filterKandidaten(kandidaten, query).slice(0, MAX_TREFFER);
}

export const BezugPicker: React.FC<{
    kandidaten: BezugKandidat[];
    query: string;
    onWaehlen: (k: BezugKandidat) => void;
    onAbbrechen: () => void;
    /**
     * Der per Tastatur markierte Vorschlag (T-50). Die Liste selbst hat nie den
     * Fokus — das Schreibfeld fängt ↑/↓/Enter ab (`auswahlTaste`) und reicht
     * den Index herein.
     */
    aktiv?: number;
    /** Maus über einem Vorschlag — das Feld übernimmt ihn als aktiven. */
    onAktiv?: (index: number) => void;
}> = ({ kandidaten, query, onWaehlen, onAbbrechen, aktiv = 0, onAktiv }) => {
    const treffer = bezugTreffer(kandidaten, query);
    const aktivRef = useAktivSichtbar(aktiv, treffer.length);
    // Kein Treffer heißt: der Nutzer tippt gerade etwas anderes. Ein leeres
    // Menü stünde ihm dabei nur im Weg.
    if (!treffer.length) return null;

    return (
        <div
            className="nb-picker"
            role="listbox"
            tabIndex={-1}
            aria-label="Bezug einfügen"
            onKeyDown={(e) => {
                if (e.key === "Escape") {
                    e.preventDefault();
                    onAbbrechen();
                }
            }}
        >
            {treffer.map((k, i) => (
                <button
                    key={`${k.art}:${k.id}`}
                    ref={i === aktiv ? aktivRef : undefined}
                    type="button"
                    role="option"
                    aria-selected={i === aktiv}
                    className={i === aktiv ? "nb-picker-item ist-aktiv" : "nb-picker-item"}
                    onMouseEnter={onAktiv ? () => onAktiv(i) : undefined}
                    onMouseDown={(e) => {
                        // mousedown statt click: ein Klick nähme dem Block
                        // zuerst den Fokus, und die Cursorposition, an der der
                        // Bezug eingesetzt werden soll, wäre verloren.
                        e.preventDefault();
                        onWaehlen(k);
                    }}
                >
                    <span className={`nb-picker-art nb-art-${k.art}`}>{ART_LABEL[k.art]}</span>
                    <span className="nb-picker-titel">{k.titel}</span>
                    {k.zusatz ? <span className="nb-picker-zusatz">{k.zusatz}</span> : null}
                </button>
            ))}
        </div>
    );
};
